import { useCallback, useEffect, useState } from "react";

/**
 * Marks a scroll container with the edges that still have content past them.
 *
 * A fade is a claim that something is cut off. Painting one unconditionally
 * makes that claim on a list that fits, which dimmed the first row of the
 * sidebar, the inspector and every tab strip at rest — the one row most likely
 * to be read. So the fade is driven by measurement instead:
 *
 *   data-scroll-start="true"  content is scrolled off the leading edge
 *   data-scroll-end="true"    content continues past the trailing edge
 *
 * Both attributes are written straight onto the node rather than lifted into
 * React state. Scrolling fires at frame rate, and a `setState` per event would
 * re-render a whole tab strip or repo tree to change two characters of CSS.
 * The attributes only drive mask stops and pseudo-element opacity, so nothing
 * they toggle can move the layout when it flips.
 *
 * Returns a *callback ref*, not an object ref: the inspector's lists and the
 * terminal strip mount and unmount with their tab, and an effect keyed on
 * `ref.current` would never re-run when that swaps.
 */
export function useScrollEdges(axis: "x" | "y" = "y") {
  const [element, setElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!element) return;

    let frame = 0;
    const measure = () => {
      frame = 0;
      const position = axis === "x" ? element.scrollLeft : element.scrollTop;
      const viewport = axis === "x" ? element.clientWidth : element.clientHeight;
      const content = axis === "x" ? element.scrollWidth : element.scrollHeight;
      // A pixel of slack: fractional layout sizes otherwise leave a fade stuck
      // on at a hard stop, which is exactly the "nothing is past this edge but
      // it looks like there is" case the attributes exist to prevent.
      element.dataset.scrollStart = position > 1 ? "true" : "false";
      element.dataset.scrollEnd = position + viewport < content - 1 ? "true" : "false";
    };
    // Coalesce to one measurement per frame; a scroll burst otherwise reads
    // layout on every event.
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };

    measure();
    element.addEventListener("scroll", schedule, { passive: true });

    // Three things change the answer: the viewport resizing, the content
    // resizing, and rows being added or removed.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(element);
    for (const child of Array.from(element.children)) observer?.observe(child);
    const mutations = typeof MutationObserver === "undefined" ? null : new MutationObserver(schedule);
    mutations?.observe(element, { childList: true, subtree: true, characterData: true });

    return () => {
      if (frame) cancelAnimationFrame(frame);
      element.removeEventListener("scroll", schedule);
      observer?.disconnect();
      mutations?.disconnect();
    };
  }, [element, axis]);

  return useCallback((node: HTMLElement | null) => setElement(node), []);
}
